import { useEffect, useState, type FormEvent } from "react";
import { useAuth } from "../contexts/AuthContext";
import { ApiError } from "../api/client";
import { listServers, pairServer, revokeServer } from "../api/auth";
import { ServerEntryCard } from "../ui/ServerEntryCard";

export function ServersPage() {
  const { api } = useAuth();
  const [rows, setRows] = useState<Awaited<ReturnType<typeof listServers>>["servers"]>([]);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  async function refresh() {
    try {
      const r = await listServers(api);
      setRows(r.servers);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "load failed");
    }
  }

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null); setOk(null);
    try {
      const r = await pairServer(api, code);
      setOk(`Paired ${r.user_server.label}`);
      setCode("");
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "pair failed");
    } finally { setBusy(false); }
  }

  async function doRevoke(id: string) {
    if (!confirm("Revoke this pairing?")) return;
    try {
      await revokeServer(api, id);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "revoke failed");
    }
  }

  return (
    <div className="vstack">
      <div>
        <h1>Prism installs</h1>
        <p className="meta">Each row is a Prism install paired to this account.</p>
      </div>

      <form className="card" onSubmit={submit}>
        <h2>Pair a new install</h2>
        <p className="meta">Open Prism's first-run screen on the home server; it shows a 12-character code.</p>
        <div className="field">
          <label htmlFor="pair">Pair code</label>
          <input id="pair" type="text" autoComplete="off" autoCapitalize="characters"
            placeholder="ABCD-EFGH-JKMN"
            value={code} required onChange={(e) => setCode(e.target.value)} />
        </div>
        {error && <div className="error">{error}</div>}
        {ok && <div className="ok">{ok}</div>}
        <button className="btn primary" type="submit" disabled={busy || code.length < 12}>
          {busy ? "Pairing…" : "Pair"}
        </button>
      </form>

      <div className="card">
        <h2>Paired</h2>
        {rows.length === 0 && <p className="meta">No paired installs.</p>}
        {rows.map((s) => (
          <div className="row" key={s.id} style={{ flexDirection: "column", alignItems: "stretch", gap: 10 }}>
            <div className="hstack" style={{ justifyContent: "space-between" }}>
              <div>
                <div className="row-title">{s.label}</div>
                <div className="row-meta">{s.server_id} · paired {s.paired_at ?? "—"}</div>
              </div>
              <button className="btn danger" onClick={() => doRevoke(s.id)}>Revoke</button>
            </div>
            <ServerEntryCard
              id={s.id}
              label={s.label}
              serverId={s.server_id}
              relationship={s.relationship}
              lastLanIp={s.last_lan_ip ?? null}
              lastLanPort={s.last_lan_port ?? null}
              lastSeenAt={s.last_seen_at}
              autoConnect={false}
              showHeader={false}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
